import React, { useState, useEffect } from "react";
import { useCreate, useGet } from 'hooks';

export default function MainPriceForm() {

  /* ================= FETCH PRICES FROM API ================= */
  const { data: priceData, loading: pricesLoading } = useGet('/api/mainprice');

  /* ================= PRICE MASTERS ================= */
  const [prices, setPrices] = useState({
    diamond: {
      natural: {
        "HI-SI": 65000,
        "GH-VS": 95000,
        "GH-VVS": 120000,
        "EF-VVS": 140000
      },
      lab: { "FG-VVS1": 25000 }
    },
    emerald: { natural: 15000, lab: 4000, above3ct: 10000 },
    ruby: { natural: 15000, lab: 4500, above3ct: 10000 },
    sapphire: { natural: 15000, above3ct: 10000 },
    semiStone: { natural: 5000, above3ct: 3000 },
    navratan: { natural: 2000, above3ct: 1500 },
    pearl: { southSea: 1000, cultured: 300 }
  });

  const [goldPrices, setGoldPrices] = useState({
    "22K": 12772,
    "18K": 10450,
    "14K": 8127,
    "9K": 5225
  });

  const [platinumPrice, setPlatinumPrice] = useState(3200);
  const { create } = useCreate('/api/mainprice');

  /* ================= LOAD PRICES FROM API ================= */
  useEffect(() => {
    if (!priceData || pricesLoading) return;

    if (priceData.priceMasters) {
      const masters = priceData.priceMasters;
      
      if (masters.diamond) {
        setPrices(prev => ({ ...prev, diamond: masters.diamond }));
      }
      if (masters.emerald) {
        setPrices(prev => ({ ...prev, emerald: masters.emerald }));
      }
      if (masters.ruby) {
        setPrices(prev => ({ ...prev, ruby: masters.ruby }));
      }
      if (masters.sapphire) {
        setPrices(prev => ({ ...prev, sapphire: masters.sapphire }));
      }
      if (masters.semiStone) {
        setPrices(prev => ({ ...prev, semiStone: masters.semiStone }));
      }
      if (masters.navratan || masters.Navratan) {
        setPrices(prev => ({ ...prev, navratan: masters.navratan || masters.Navratan }));
      }
      if (masters.pearl) {
        setPrices(prev => ({ ...prev, pearl: masters.pearl }));
      }
      if (masters.gold) {
        setGoldPrices(masters.gold);
      }
    }

    if (priceData.platinumPrice) {
      setPlatinumPrice(priceData.platinumPrice);
    }
  }, [priceData, pricesLoading]);

  /* ================= MAKING CHARGES MASTER ================= */

const makingRates = {
  Rings: { gold: 1700, platinum: 2300 },
  "Nose Rings": { gold: 2500, platinum: 3500 },
  Earrings: { gold: 2000, platinum: 2500 },
  Pendants: { gold: 2000, platinum: 2500 },
  "Belly Rings": { gold: 2700, platinum: 3500 },
  Industrial: { gold: 2700, platinum: 3500 },
  Bracelets: { gold: 2200, platinum: 2700 }
};


  /* ================= CALCULATOR STATE ================= */

 const [calc, setCalc] = useState({
  jewelleryType: "Rings",
  stoneItems: [{ stone: "diamond", variant: "natural", quality: "HI-SI", carat: 1 }],
  goldItems: [{ purity: "14K", weight: 0 }],
  platinumWeight: 0,
  total: 0
});


  /* ================= CALCULATIONS ================= */

  const calculateStoneRowPrice = (s) => {
    if (!s.carat) return 0;

    if (s.stone === "diamond") {
      return (prices.diamond[s.variant]?.[s.quality] || 0) * s.carat;
    }

    if (s.stone === "emerald" || s.stone === "ruby") {
      return (s.carat > 3 ? prices[s.stone].above3ct : prices[s.stone][s.variant]) * s.carat;
    }

    if (s.stone === "sapphire" || s.stone === "semiStone" || s.stone === 'opal') {
      return (s.carat > 3 ? prices[s.stone].above3ct : prices[s.stone].natural) * s.carat;
    }

    if (s.stone === "pearl") {
      return prices.pearl[s.variant] * s.carat;
    }

    return 0;
  };

 const calculateTotalPrice = (data) => {
  let total = 0;

  // Stones
  data.stoneItems.forEach(s => {
    total += calculateStoneRowPrice(s);
  });

  // Gold value
  let totalGoldWeight = 0;
  data.goldItems.forEach(g => {
    totalGoldWeight += g.weight;
    total += (goldPrices[g.purity] || 0) * g.weight;
  });

  // Platinum value
  total += platinumPrice * data.platinumWeight;

 
  total += totalGoldWeight * (makingRates[data.jewelleryType]?.gold || 0);
  total += data.platinumWeight * (makingRates[data.jewelleryType]?.platinum || 0);

  return total;
};

  const GST_PERCENT = 3;

const submitCalculation = async () => {
  const subtotal = calc.total;
  const gstAmount = Math.round((subtotal * GST_PERCENT) / 100);
  const finalPrice = subtotal + gstAmount;

  const payload = {
    priceMasters: {
      diamond: prices.diamond,
      emerald: prices.emerald,
      ruby: prices.ruby,
      sapphire: prices.sapphire,
      semiStone: prices.semiStone,
      navratan: prices.navratan,
      pearl: prices.pearl,
      gold: goldPrices,
      platinum: platinumPrice
    },

    jewelleryType: calc.jewelleryType,

    stoneItems: calc.stoneItems.map(s => ({
      ...s,
      price: calculateStoneRowPrice(s)
    })),

    goldItems: calc.goldItems.map(g => ({
      ...g,
      price: goldPrices[g.purity] * g.weight
    })),

    platinumWeight: calc.platinumWeight,
    platinumPrice,

    makingCharges: {
      gold:
        calc.goldItems.reduce((s, g) => s + g.weight, 0) *
        (makingRates[calc.jewelleryType]?.gold || 0),
      platinum:
        calc.platinumWeight *
        (makingRates[calc.jewelleryType]?.platinum || 0)
    },

    subtotal,
    gstPercent: GST_PERCENT,
    gstAmount,
    totalPrice: finalPrice,
    currency: "INR"
  };

    try {
      await create(payload, { revalidate: false });
      alert('Pricing saved successfully ✅');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to save pricing', err);
      const message = err?.response?.data?.message || err?.response?.data || err.message || 'Failed to save pricing';
      alert(String(message));
    }
};




  const updateCalc = (updated) => {
    updated.total = calculateTotalPrice(updated);
    setCalc(updated);
  };

  /* ================= UI ================= */

  return (
    <div style={{ maxWidth: 900 }}>

      <h2>Main Price Form</h2>

      {pricesLoading && <p>Loading prices...</p>}

      {/* ================= GEMSTONE PRICE MASTER ================= */}
      {Object.entries(prices).map(([stone, values]) => (
        <Section key={stone} title={`${stone.toUpperCase()} Price Master`}>
          {Object.entries(values).map(([k, v]) =>
            typeof v === "number" ? (
              <PriceInput
                key={k}
                label={k}
                value={v}
                onChange={(val) =>
                  setPrices(prev => ({
                    ...prev,
                    [stone]: { ...prev[stone], [k]: Number(val) }
                  }))
                }
              />
            ) : (
              Object.entries(v).map(([subK, subV]) => (
                <PriceInput
                  key={subK}
                  label={`${k} ${subK}`}
                  value={subV}
                  onChange={(val) =>
                    setPrices(prev => ({
                      ...prev,
                      [stone]: {
                        ...prev[stone],
                        [k]: { ...prev[stone][k], [subK]: Number(val) }
                      }
                    }))
                  }
                />
              ))
            )
          )}
        </Section>
      ))}

      {/* ================= GOLD PRICE MASTER ================= */}
      <Section title="Gold Price Master (₹ per gram)">
        {Object.keys(goldPrices).map(purity => (
          <PriceInput
            key={purity}
            label={purity}
            value={goldPrices[purity]}
            onChange={(v) =>
              setGoldPrices(prev => ({ ...prev, [purity]: Number(v) }))
            }
          />
        ))}
      </Section>

      {/* ================= PLATINUM PRICE MASTER ================= */}
      <Section title="Platinum Price Master (₹ per gram)">
        <PriceInput
          label="Platinum"
          value={platinumPrice}
          onChange={(v) => setPlatinumPrice(Number(v))}
        />
      </Section>

<button
  style={{ padding: 12, fontSize: 16, marginTop: 20 }}
  onClick={submitCalculation}
>
  💾 Save Price 
</button>


    </div>
    
  );



}

/* ================= UI HELPERS ================= */

const Section = ({ title, children }) => (
  <div style={{ border: "1px solid #ddd", padding: 20, borderRadius: 10, marginBottom: 25 }}>
    <h3>{title}</h3>
    {children}
  </div>
);

const PriceInput = ({ label, value, onChange }) => (
  <div style={{ marginBottom: 12 }}>
    <label>{label}</label>
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: "100%", padding: 8, marginTop: 4 }}
    />
  </div>
);
